import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import KitFormModal from '../../components/feature/KitFormModal';
import Header from '../../components/feature/Header';
import { usePageTracking } from '../../hooks/usePageTracking';

export default function HomePage() {
  const [isKitFormOpen, setIsKitFormOpen] = useState(false);

  // Track page visit
  usePageTracking('home');

  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('header');
      if (header) {
        if (window.scrollY > 50) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleKitFormClick = (e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsKitFormOpen(true);
  };

  return (
    <>
      {/* Custom Header for Homepage */}
      <Header onOpenKitForm={() => handleKitFormClick()} />
      
      <div className="min-h-screen bg-[#0D1B2A] overflow-x-hidden">
        {/* Kit Form Modal */}
        <KitFormModal isOpen={isKitFormOpen} onClose={() => setIsKitFormOpen(false)} />

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] via-[#1B263B] to-[#0D1B2A]"></div>
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(212, 175, 55, 0.1) 10px, rgba(212, 175, 55, 0.1) 20px)'
          }}></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-20 relative z-10 w-full">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left Content */}
              <div className="space-y-6 sm:space-y-8">
                <div className="inline-block">
                  <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.2em] uppercase">
                    Tactical Financial Defense
                  </span>
                </div>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#F5F5F0] leading-tight">
                  Build Your Fallback Funds Before The Next Surprise Hits
                </h1>
                
                <p className="text-base sm:text-lg text-[#C5C9D1] leading-relaxed">
                  Use a Stealth Capital Stack of 0% credit lines to quietly build $50K–$150K in backup liquidity—without touching your home equity or wrecking your credit score.
                </p>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl"></i>
                    </div>
                    <span className="text-[#F5F5F0] text-sm sm:text-base">0% intro interest for 12–21 months</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl"></i>
                    </div>
                    <span className="text-[#F5F5F0] text-sm sm:text-base">100% unsecured – home/car not collateral</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-checkbox-circle-fill text-[#D4AF37] text-xl"></i>
                    </div>
                    <span className="text-[#F5F5F0] text-sm sm:text-base">Some lines don't report to personal credit</span>
                  </div>
                </div>
                
                <div className="pt-4">
                  <a 
                    href="#"
                    onClick={handleKitFormClick}
                    className="inline-block bg-[#D4AF37] hover:bg-[#C19A2E] text-black font-bold px-6 sm:px-10 lg:px-12 py-3 sm:py-4 rounded-full text-sm sm:text-base lg:text-lg transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    CHECK MY ELIGIBILITY
                  </a>
                  <p className="text-[#B8964F] text-xs sm:text-sm italic mt-3 text-center lg:text-left">
                    No hard inquiry to check.
                  </p>
                </div>
              </div>
              
              {/* Right Visual */}
              <div className="relative hidden lg:block">
                {/* Vault Door Image */}
                <div className="relative">
                  <div className="absolute inset-0 bg-[#D4AF37]/20 rounded-full blur-3xl"></div>
                  <button 
                    onClick={handleKitFormClick}
                    className="relative block w-[500px] h-[500px] mx-auto rounded-full overflow-hidden shadow-2xl ring-8 ring-[#D4AF37]/30 transition-all duration-500 hover:scale-110 hover:shadow-[0_25px_50px_-12px_rgba(212,175,55,0.5)] cursor-pointer group border-none bg-transparent p-0"
                  >
                    <img
                      src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/786c25526f56e48f1b481e6c12bbe48b.png"
                      alt="Vault Door"
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="bg-[#F8F7F4] py-16 sm:py-20 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-20 max-w-5xl text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D1B2A] mb-6 sm:mb-8">
              Banks Can Freeze. Bills Don't.
            </h2>
            <p className="text-base sm:text-lg text-[#3A3F4B] leading-relaxed max-w-3xl mx-auto mb-8 sm:mb-10">
              Tax hikes. Emergency repairs. Income shocks. Medical bills. Market downturns. When surprises hit, you need backup liquidity fast—not a loan application that takes weeks or a HELOC that puts your home at risk. A Stealth Capital Stack gives you standby funds ready when you need them most.
            </p>
            <a 
              href="#"
              onClick={handleKitFormClick}
              className="inline-block border-2 border-[#0D1B2A] text-[#0D1B2A] hover:bg-[#D4AF37] hover:border-[#D4AF37] hover:text-black font-semibold px-6 sm:px-10 py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              Start Your Eligibility Check
            </a>
          </div>
        </section>

        {/* Stealth Capital Stack Section */}
        <section className="bg-[#1E2530] py-16 sm:py-20 lg:py-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-20">
            <div className="grid md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 max-w-6xl mx-auto">
              {/* Column 1 */}
              <div className="text-center space-y-4 sm:space-y-5">
                <div className="w-16 h-16 flex items-center justify-center mx-auto">
                  <i className="ri-percent-line text-[#D4AF37] text-4xl sm:text-5xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#F5F5F0]">Interest Advantage</h3>
                <p className="text-sm sm:text-base text-[#C5C9D1] leading-relaxed">
                  Stack multiple 0% intro APR credit lines for 12–21 months. Use the money interest-free, then rotate or pay down before rates kick in.
                </p>
              </div>
              
              {/* Column 2 */}
              <div className="text-center space-y-4 sm:space-y-5 md:border-l md:border-r border-[#D4AF37]/30 md:px-6 lg:px-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto">
                  <i className="ri-shield-check-line text-[#D4AF37] text-4xl sm:text-5xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#F5F5F0]">Asset Firewall</h3>
                <p className="text-sm sm:text-base text-[#C5C9D1] leading-relaxed">
                  100% unsecured credit. Your home, car, and retirement accounts are never used as collateral. No liens. No risk to your core assets.
                </p>
              </div>
              
              {/* Column 3 */}
              <div className="text-center space-y-4 sm:space-y-5">
                <div className="w-16 h-16 flex items-center justify-center mx-auto">
                  <i className="ri-eye-off-line text-[#D4AF37] text-4xl sm:text-5xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#F5F5F0]">Phantom Tradelines</h3>
                <p className="text-sm sm:text-base text-[#C5C9D1] leading-relaxed">
                  Certain business credit lines don't report to your personal credit bureau—giving you liquidity without impacting your personal credit score.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="relative bg-[#F8F7F4] py-16 sm:py-24 lg:py-32 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-20">
            <div className="text-center mb-12 sm:mb-16 lg:mb-20">
              <div className="inline-block mb-4">
                <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.2em] uppercase">
                  Fast, Flexible, and Stress-Free
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#0D1B2A]">
                How It Works
              </h2>
            </div>
            
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
                {/* Step 1 */}
                <div className="relative group">
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#D4AF37] h-full flex flex-col">
                    <div className="relative mb-6 sm:mb-8">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 mx-auto bg-gradient-to-br from-[#0D1B2A] to-[#1B263B] rounded-full flex items-center justify-center shadow-xl relative z-10 group-hover:scale-110 transition-transform duration-500">
                        <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#D4AF37]">1</span>
                      </div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 bg-[#D4AF37]/10 rounded-full blur-xl"></div>
                    </div>
                    
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0D1B2A] mb-3 sm:mb-4 text-center">Apply</h3>
                    
                    <div className="flex-grow flex items-center">
                      <p className="text-[#3A3F4B] leading-relaxed text-sm sm:text-base text-center">
                        <i className="ri-arrow-right-line text-[#D4AF37] mr-2"></i>
                        Share your name, email, and phone number for an instant approval number.
                      </p>
                    </div>
                  </div>
                  
                  {/* Connector Line */}
                  <div className="hidden md:block absolute top-14 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#D4AF37] to-transparent z-0"></div>
                </div>
                
                {/* Step 2 */}
                <div className="relative group">
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#D4AF37] h-full flex flex-col">
                    <div className="relative mb-6 sm:mb-8">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 mx-auto bg-gradient-to-br from-[#3A4A5C] to-[#A68A5E] rounded-full flex items-center justify-center shadow-xl relative z-10 group-hover:scale-110 transition-transform duration-500">
                        <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#D4AF37]">2</span>
                      </div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 bg-[#D4AF37]/10 rounded-full blur-xl"></div>
                    </div>
                    
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0D1B2A] mb-3 sm:mb-4 text-center">FREE Consultation</h3>
                    
                    <div className="flex-grow flex items-center">
                      <p className="text-[#3A3F4B] leading-relaxed text-sm sm:text-base text-center">
                        <i className="ri-arrow-right-line text-[#D4AF37] mr-2"></i>
                        Speak with our experts to assess best options and initiate the process.
                      </p>
                    </div>
                  </div>
                  
                  {/* Connector Line */}
                  <div className="hidden md:block absolute top-14 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#D4AF37] to-transparent z-0"></div>
                </div>
                
                {/* Step 3 */}
                <div className="relative group">
                  <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#D4AF37] h-full flex flex-col">
                    <div className="relative mb-6 sm:mb-8">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 mx-auto bg-gradient-to-br from-[#D4AF37] to-[#C19A2E] rounded-full flex items-center justify-center shadow-xl relative z-10 group-hover:scale-110 transition-transform duration-500">
                        <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black">3</span>
                      </div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 bg-[#D4AF37]/20 rounded-full blur-xl"></div>
                    </div>
                    
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0D1B2A] mb-3 sm:mb-4 text-center">Get Funded</h3>
                    
                    <div className="flex-grow flex items-center">
                      <p className="text-[#3A3F4B] leading-relaxed text-sm sm:text-base text-center">
                        <i className="ri-arrow-right-line text-[#D4AF37] mr-2"></i>
                        We connect you to unsecured credit lines, delivering funds within 7–14 business days.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bottom CTA */}
              <div className="text-center mt-12 sm:mt-16">
                <a 
                  href="#"
                  onClick={handleKitFormClick}
                  className="inline-block bg-[#0D1B2A] hover:bg-[#D4AF37] text-white hover:text-black font-bold px-8 sm:px-10 lg:px-12 py-3 sm:py-4 rounded-full text-base sm:text-lg transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Get Started Now
                </a>
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
        </section>

        {/* Authority Section */}
        <section className="relative bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] py-12 sm:py-16 lg:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-20">
            <div className="max-w-5xl mx-auto bg-[#1E2530]/50 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-10 lg:p-12 border border-[#D4AF37]/20">
              <div className="grid md:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 items-center">
                <div className="md:col-span-4">
                  <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 mx-auto rounded-full border-4 border-[#D4AF37] overflow-hidden">
                    <img 
                      src="https://static.readdy.ai/image/93c3e079cd1f297eadf6098c3b0ef55d/dadaf59f289a861c44051d9874a96e48.jpeg"
                      alt="Matt - Financial Strategist"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="md:col-span-8 space-y-4 sm:space-y-5">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#F5F5F0] italic">
                    "I Treat This Like Ammunition."
                  </h3>
                  <p className="text-base sm:text-lg text-[#C5C9D1] leading-relaxed">
                    I've been stacking 0% credit lines for over a decade. When opportunity knocks—or when trouble hits—I don't scramble for a loan or drain my savings. I deploy capital from my Stealth Capital Stack. It's there when I need it, invisible when I don't. That's the difference between reacting and being ready.
                  </p>
                  <p className="text-[#D4AF37] font-semibold text-sm sm:text-base">
                    — Matt, Real Estate Investor & FallbackFunds Strategist
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Who This Is For Section */}
        <section id="who-we-help" className="bg-[#EDEAE3] py-16 sm:py-20 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D1B2A] text-center mb-12 sm:mb-16">
              Who This Is For
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
              {/* Card 1 */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
                <div className="w-16 h-16 flex items-center justify-center mb-4 sm:mb-5">
                  <i className="ri-home-4-line text-[#D4AF37] text-4xl sm:text-5xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0D1B2A] mb-3 sm:mb-4">
                  Homeowners 50+
                </h3>
                <p className="text-sm sm:text-base text-[#3A3F4B] leading-relaxed">
                  Build a backup fund for surprise expenses—roof repairs, medical bills, family emergencies—without touching your home equity or retirement savings.
                </p>
              </div>
              
              {/* Card 2 */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
                <div className="w-16 h-16 flex items-center justify-center mb-4 sm:mb-5">
                  <i className="ri-building-line text-[#D4AF37] text-4xl sm:text-5xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0D1B2A] mb-3 sm:mb-4">
                  Real Estate Investors
                </h3>
                <p className="text-sm sm:text-base text-[#3A3F4B] leading-relaxed">
                  Fund down payments, rehab projects, or bridge gaps between deals without draining your cash reserves or waiting on traditional financing.
                </p>
              </div>
              
              {/* Card 3 */}
              <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer">
                <div className="w-16 h-16 flex items-center justify-center mb-4 sm:mb-5">
                  <i className="ri-briefcase-line text-[#D4AF37] text-4xl sm:text-5xl"></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0D1B2A] mb-3 sm:mb-4">
                  Business Owners
                </h3>
                <p className="text-sm sm:text-base text-[#3A3F4B] leading-relaxed">
                  Cover payroll, inventory, or marketing during choppy times. Keep your business running smoothly without high-interest loans or giving up equity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Strip */}
        <section className="relative bg-gradient-to-r from-[#D4AF37] to-[#C19A2E] py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(0, 0, 0, 0.1) 20px, rgba(0, 0, 0, 0.1) 40px)'
          }}></div>
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-20 text-center relative z-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#0D1B2A] mb-3 sm:mb-4">
              How Much Backup Liquidity Could You Build?
            </h2>
            <p className="text-base sm:text-lg text-[#0D1B2A]/80 mb-6 sm:mb-8 lg:mb-10">
              Most qualify for $50K–$150K. 60-second estimate.
            </p>
            <a 
              href="#"
              onClick={handleKitFormClick}
              className="inline-block bg-[#0D1B2A] hover:bg-[#1B263B] text-white font-bold px-8 sm:px-12 lg:px-14 py-3 sm:py-4 lg:py-5 rounded-full text-base sm:text-lg lg:text-xl transition-all duration-300 hover:scale-105 shadow-2xl whitespace-nowrap cursor-pointer"
            >
              START THE 60-SECOND CHECK
            </a>
          </div>
        </section>
      </div>
    </>
  );
}